import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { UserModel } from 'src/app/models/user.model';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { ActivatedRoute } from '@angular/router';
import { AccountModel } from 'src/app/models/account.model';
import { NgForm } from '@angular/forms';
import { AccountsSelectorComponent } from '../../accounts/accounts-selector/accounts-selector.component';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { TradeModel } from '../../trades/trades.model';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-users-edit',
  templateUrl: './users-edit.component.html',
  styleUrls: ['./users-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class UsersEditComponent implements OnInit {
  user: UserModel = new UserModel();

  trades: TradeModel[] = [];

  accountsDisplayedColumns: string[] = [
    'account_id',
    'name',
    'subscription',
    'date_created',
    // Membership approval state (SC-301). The mirror of the same column on the account's Employees tab:
    // this list has always included unapproved requests, they were just indistinguishable from real
    // memberships, so the reason a user could not reach an account was invisible on the very screen
    // support opens to find it.
    'approval_status',
    'rbac_roles',
    'actions'
  ];

  accountsDataSource = new UserAccountsDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  inProgress: boolean = false;

  // The saved super admin flag. The System tab's checkbox changes user.is_sitesoft_admin before it is
  // saved, so the Partner tab is gated on the persisted value instead.
  isPersistedSuperAdmin: boolean = false;

  passwordValidated: boolean = false;
  showPasswordInputs: boolean = false;
  passwordFieldType: string = 'password';
  confirmPasswordFieldType: string = 'password';

  // Mobile field Country Code selector values
  phone_input_id: string = 'phone-1';
  phone_number: string = '';
  phone_country_code: string = '';
  phone_dial_code: string = '';
  phone_e164: string = '';
  phone_error_state: boolean = false;

  constructor(
    public app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private route: ActivatedRoute,
    private rbac: RBACService
  ) {}

  ngOnInit() {
    this.user.id = Number(this.route.snapshot.params['id']);
    if (this.user.id) {
      this.getUser();
    }

    // Get trades from the API.
    this.getTrades();
  }

  ngAfterViewInit() {
    if (!this.user.id) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.accountsDataSource.limit = this.paginator.pageSize;
      this.accountsDataSource.offset = this.paginator.pageIndex;
      this.accountsDataSource.order_by = this.sort.active;
      this.accountsDataSource.order = this.sort.direction;

      this.accountsDataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  private async getTrades() {
    this.trades = await this.utils.getTrades();
  }

  /**
   * Get Output values from phone input component.
   */
  getOutputPhoneDetails($event) {
    if ($event) {
      let phone = $event;
      this.phone_number =
        phone.hasOwnProperty('number') && phone.number !== null
          ? phone.number
          : '';
      this.phone_country_code =
        phone.hasOwnProperty('countryCode') && phone.countryCode !== null
          ? phone.countryCode
          : '';
      this.phone_dial_code =
        phone.hasOwnProperty('dialCode') && phone.dialCode !== null
          ? phone.dialCode
          : '';
      this.phone_e164 =
        phone.hasOwnProperty('e164Number') && phone.e164Number !== null
          ? phone.e164Number
          : '';
      this.phone_error_state =
        phone.hasOwnProperty('errorState') && phone.errorState !== null
          ? phone.errorState
          : true;
    }
  }

  /**
   * Set Input values for phone input component.
   */
  private initPhoneDetails() {
    this.phone_number = this.user.mobile;
    this.phone_country_code = this.user.mobile_country_code;
    this.phone_dial_code = this.user.mobile_dial_code;
    this.phone_e164 = this.user.mobile_e164;
  }

  getUser() {
    return this.api
      .makeRequest('get', `v2/admin/users/${this.user.id}`)
      .then((response: UserModel) => {
        this.user.apply(response);
        this.isPersistedSuperAdmin = !!response.is_sitesoft_admin;
        this.initPhoneDetails();
        // Set the user id in the datasource and get the data
        this.accountsDataSource.userId = this.user.id;
        this.accountsDataSource.getData();
      });
  }

  // Redirect the user to the specified account by id
  onRedirectToAccount(employerId: number) {
    this.utils.refreshPage(['/', 'admin', 'accounts', employerId, 'edit']);
  }

  onSubmit(form: NgForm) {
    // Check form validation.
    if (!form.valid) {
      this.utils.showModal(
        'Form Validation Failed',
        'Please enter all required fields.'
      );
      return;
    }

    // Check Error states of phone input then continue submission
    if (this.phone_error_state) {
      if (this.phone_number.length < 3) {
        this.utils.showModal(
          'Form Validation Failed',
          'A Phone number must be at least 3 characters long.'
        );
      } else {
        this.utils.showModal(
          'Form Validation Failed',
          'Please enter all required fields.'
        );
      }
      return;
    }

    // Terminate early if another process is in progress.
    if (this.inProgress) {
      return;
    }

    // Update User details with Mobile field Country Code selector values
    this.user.mobile = this.phone_number;
    this.user.mobile_country_code = this.phone_country_code;
    this.user.mobile_dial_code = this.phone_dial_code;
    this.user.mobile_e164 = this.phone_e164;

    this.inProgress = true;

    if (this.user.id) {
      this.api
        .makeRequest('put', `v2/admin/users/${this.user.id}`, this.user)
        .then((response: UserModel) => {
          this.user.apply(response);
          // The details save sends the whole user, including the super admin flag.
          this.isPersistedSuperAdmin = !!response.is_sitesoft_admin;
          // Refresh the linked accounts list, matching every other handler in this component that
          // changes a link (onSelectEmployer, the link and unlink handlers). This was the only mutating
          // path that did not, so a save that changed an account name or moved the user to a different
          // employer left the table showing the previous values until the page was reloaded.
          //
          // The list is its own paginated endpoint (v2/admin/users-accounts), so applying the response
          // onto the user model does not touch it.
          this.accountsDataSource.getData();
          this.utils.showModal(
            'User Updated',
            'The user was sucessfully updated.'
          );
        })
        .finally(() => {
          this.inProgress = false;
        });
    } else {
      this.api
        .makeRequest('post', `v2/admin/users`, this.user)
        .then((response: UserModel) => {
          this.utils.showToast('The new user was successfully created.');
          this.utils.refreshPage(['/admin', 'users', response.id, 'edit']);
        })
        .finally(() => {
          this.inProgress = false;
        });
    }
  }

  /**
   * Send an API request to update the user's email, username or password.
   * @param form
   */
  onUpdatePassword(form: NgForm) {
    // Make sure all required fields are filled out.
    if (!form.valid) {
      this.utils.showModal(
        'Form Validation Failed',
        'Please enter all required fields.'
      );
      return;
    }
    // Only existing users may be updated.
    if (!this.user.id) {
      this.utils.showModal(
        'Error',
        'You can only update the password for a pre-existing user.'
      );
      return;
    }
    // Change the in progress flag to true.
    this.inProgress = true;
    // Send the API request to update the user's email, username or password.
    this.api
      .makeRequest(
        'put',
        `v2/admin/users/${this.user.id}/update-password`,
        this.user
      )
      .then((response: UserModel) => {
        // Apply the response to the user model.
        this.user.apply(response);
        this.utils.showModal(
          'Password Updated',
          "The user's login credentials was successfully updated."
        );
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  /**
   * Save the system (danger zone) settings, e.g. the super admin flag. Only applies to an existing
   * user. Reuses the same admin update endpoint as onSubmit().
   */
  onSaveSystem(): void {
    if (!this.user.id) {
      return;
    }
    if (this.inProgress) {
      return;
    }

    const doSave = (): void => {
      this.inProgress = true;
      this.api
        .makeRequest('put', `v2/admin/users/${this.user.id}`, this.user)
        .then((response: UserModel) => {
          this.user.apply(response);
          this.isPersistedSuperAdmin = !!response.is_sitesoft_admin;
          this.utils.showToast('The user was successfully updated.');
        })
        .finally(() => {
          this.inProgress = false;
        });
    };

    // Safety net: confirm before granting super admin access. Disabling it saves without a prompt.
    if (this.user.is_sitesoft_admin) {
      this.utils.showModal(
        'Confirm Super Admin Access',
        'You are about to grant ' +
          this.utils.getAppName() +
          ' support (super admin) access to this user. This grants platform-wide system administration privileges across all accounts. Are you sure?',
        doSave
      );
    } else {
      doSave();
    }
  }

  /**
   * Warn the system administrator when enabling super admin (support) access. The change is only
   * persisted when the form is saved.
   */
  onToggleSuperAdmin(isChecked: boolean): void {
    if (isChecked) {
      this.utils.showModal(
        'Super Admin Access',
        'Super admin access grants platform-wide system administration privileges across all accounts. It is intended only for ' +
          this.utils.getAppName() +
          ' support staff, never for clients or regular users. This change will apply when you save.'
      );
    }
  }

  /**
   * Get the RBAC roles that can still be assigned for a row (the account's roles the user does not
   * already hold). Each account has its own independent set of roles.
   *
   * @param row The users-accounts row (carries account_id, user_id, rbac_roles, available_rbac_roles).
   */
  getAssignableRoles(row: any): any[] {
    const assignedIds: number[] = (row.rbac_roles || []).map((r: any) => r.id);
    return (row.available_rbac_roles || []).filter(
      (r: any) => !assignedIds.includes(r.id)
    );
  }

  /**
   * Assign a single RBAC role to the user for a specific account (super admin support action).
   *
   * @param accountId The account the role belongs to.
   * @param userId The user to assign the role to.
   * @param role The RBAC role ({ id, name }).
   */
  onAddRbacRole(accountId: number, userId: number, role: any) {
    this.api
      .makeRequest(
        'post',
        `v2/admin/users-accounts/${accountId}/${userId}/rbac-role/${role.id}`
      )
      .then(() => {
        this.utils.showToast('The RBAC role was assigned.');
        this.accountsDataSource.getData();
      })
      .catch((_error) => {
        this.utils.handleAPIErrors(_error);
      });
  }

  /**
   * Unlink a single RBAC role from the user for a specific account (super admin support action).
   *
   * @param accountId The account the role belongs to.
   * @param userId The user to remove the role from.
   * @param role The RBAC role ({ id, name }).
   */
  onUnlinkRbacRole(accountId: number, userId: number, role: any) {
    this.utils.showModal(
      'Unlink RBAC Role',
      'Are you sure you want to unlink the "' +
        role.name +
        '" role from this user in this account?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v2/admin/users-accounts/${accountId}/${userId}/rbac-role/${
              role.id
            }`
          )
          .then(() => {
            this.utils.showToast('The RBAC role was unlinked.');
            this.accountsDataSource.getData();
          })
          .catch((_error) => {
            this.utils.handleAPIErrors(_error);
          });
      }
    );
  }

  onSelectAccounts() {
    this.utils.showComponentDialog(
      AccountsSelectorComponent,
      {
        multiple: true,
        returnObjects: true,
        selected: this.user.accounts
      },
      {
        disableClose: false,
        width: '1200px'
      },
      (accounts: AccountModel[]) => {
        if (accounts && accounts.length > 0) {
          let accountNames: string[] = [];
          let accountIds: number[] = [];
          accounts.forEach((account) => {
            accountNames.push(account.name);
            accountIds.push(account.id);
          });
          this.utils.showModal(
            'Link Selected Accounts',
            'Are you sure you want to link the selected accounts (' +
              accountNames.join(', ') +
              ') with the user?',
            () => {
              this.inProgress = true;
              this.api
                .makeRequest('post', `v2/admin/users-accounts`, {
                  account_ids: accountIds,
                  user_ids: [this.user.id]
                })
                .then((response: any) => {
                  // Warn when the partner role could not be applied in a linked account (api-spec 6.7).
                  this.utils.showToast(
                    'The accounts were linked to the user.' +
                      this.rbac.partnerSyncFailureNotice(response)
                  );
                  this.accountsDataSource.getData(true);
                  this.inProgress = false; // TBD: test to see if we need to place it in finally instead.
                })
                .then(() => {
                  this.getUser();
                });
            }
          );
        }
      }
    );
  }

  onSelectEmployer() {
    this.utils.showComponentDialog(
      AccountsSelectorComponent,
      {
        multiple: false,
        returnObjects: true,
        selected: [
          {
            id: this.user.account_id
          }
        ]
      },
      {
        disableClose: false,
        width: '1200px'
      },
      (account: any) => {
        if (account) {
          this.utils.showModal(
            'Selected Employer',
            'Are you sure you want to link the selected account, ' +
              account.name +
              ', as the employer of the new user?',
            () => {
              this.user.account_id = account.id;
              this.user.employer = account;
            }
          );
        }
      }
    );
  }

  /**
   * Plain-language explanation of a membership state, for support reading this screen.
   *
   * Falls back to the pending wording when the value is missing rather than showing nothing, because an
   * absent status is the fail-closed database default and means the same thing in practice.
   */
  getMembershipTooltip(status: string): string {
    switch (status) {
      case 'approved':
        return 'This user is a member of the account and can see its data.';
      case 'pending':
        return 'This user has asked to join and is waiting for the account to approve them. They cannot see any of the account\'s data yet.';
      case 'declined':
        return 'The account refused this request. The user cannot see any of the account\'s data.';
      default:
        return 'This membership has not been approved, so the user cannot see the account\'s data.';
    }
  }

  /**
   * Approve a membership on the account's behalf (super admin support action).
   *
   * The confirmation names the account and lists the consequences rather than asking a bare "are you
   * sure". Support is acting for somebody else here, and it is not a reversible tidy-up: the user is
   * emailed and gains sight of that account's data, so approving the wrong row discloses one customer's
   * information to the wrong person.
   *
   * A modal rather than the quick-actions popover used by Delete. The popover is anchored to the button and
   * has no width constraint, so longer copy grows sideways until it leaves the page. Delete fits in one
   * line, this does not, and the part that does not fit is the part worth reading.
   */
  onApproveMembership(link: any) {
    this.utils.showModal(
      'Approve Membership',
      `<p>Approve this user as a member of <b>${link.name}</b>, on that account's behalf?</p>` +
        '<p>This will:</p>' +
        '<ul>' +
        '<li>Email them to say they were approved.</li>' +
        '<li>Give them their default role in that account.</li>' +
        '<li>Let them see that account\'s data.</li>' +
        '</ul>',
      () => {
        this.inProgress = true;
        this.api
          .makeRequest(
            'put',
            `v2/admin/users-accounts/${link.account_id}/${link.user_id}/approve`
          )
          .then(() => {
            this.utils.showToast('The membership was approved.');
            this.accountsDataSource.getData();
            this.getUser();
          })
          .catch((_error) => {
            this.utils.handleAPIErrors(_error);
          })
          .finally(() => {
            this.inProgress = false;
          });
      }
    );
  }

  onDestroyUserAccount(link: any, evt: any) {
    this.utils.showQuickActions(
      evt.target,
      `Are you sure you want to remove the '${link.name}' user account link?`,
      [
        {
          text: 'Yes',
          handler: () => {
            this.inProgress = true;
            this.api
              .makeRequest(
                'delete',
                `v2/admin/users-accounts/${link.account_id}/${link.user_id}`
              )
              .then(() => {
                this.utils.showToast('The user account link was removed.');
                this.accountsDataSource.getData(true);
                this.getUser();
              })
              .catch((_error) => {
                // Show the API's reason, e.g. the last administrator cannot be removed.
                this.utils.handleAPIErrors(_error);
              })
              .finally(() => {
                this.inProgress = false;
              });
          }
        },
        {
          text: 'No',
          handler: () => {}
        }
      ]
    );
  }

  getOutputPasswordValidated($event) {
    this.passwordValidated = $event;
  }

  /**
   * Toggles the visibility of the password field based on the given field parameter.
   *
   * @param {string} field - The name of the field to toggle visibility for ('password' or 'confirmPassword').
   * @returns {void}
   */
  togglePasswordVisibility(field: string): void {
    if (field === 'password') {
      this.passwordFieldType =
        this.passwordFieldType === 'password' ? 'text' : 'password';
    } else if (field === 'confirmPassword') {
      this.confirmPasswordFieldType =
        this.confirmPasswordFieldType === 'password' ? 'text' : 'password';
    }
  }
}

export class UserAccountsDataSource extends ApiDataSource {
  order_by = 'name';
  order = 'asc';

  userId: number;

  getData(resetOffset: boolean = false) {
    this.makeRequest(`v2/admin/users-accounts`, resetOffset, {
      admin_user_id: this.userId
    });
  }
}
